// src/pages/procurement/PayablesPage.jsx
//
// Outstanding supplier balances (aging summary) for Procurement. Cloned from
// src/pages/offlineSales/ReceivablesPage.jsx — same layout, client-side search/pagination
// since the backend's getAgingSummary (rettzy-backend .../procurement/services/payables.service.js)
// returns the whole business's open payables in one call.
//
// Response shape: { buckets, totalOutstanding, suppliers: [{ supplierId, supplierName,
// supplierPhone, totalOutstanding, buckets }] } — note the field is "suppliers", not
// "customers" as on the sell-side Receivables response.
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Users, FileText, Clock, User, Phone } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import { getPayablesAgingSummary } from '../../services/api/procurement/payables';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import tb from './components/ProcurementTable.module.css';
import styles from './PayablesPage.module.css';

const BUCKET_KEYS = ['0-30', '31-60', '61-90', '90+'];
const BUCKET_LABEL_KEYS = {
  '0-30': 'aging0to30',
  '31-60': 'aging31to60',
  '61-90': 'aging61to90',
  '90+': 'aging90plus',
};

const PayablesPage = () => {
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const { error: showError } = useAlert();

  const [summary, setSummary] = useState({ buckets: {}, totalOutstanding: 0, suppliers: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    pageSizeOptions: [20, 50, 100],
  });

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSummary = async () => {
    try {
      setIsLoading(true);
      const result = await getPayablesAgingSummary();
      setSummary({
        buckets: result.buckets || {},
        totalOutstanding: result.totalOutstanding || 0,
        suppliers: result.suppliers || [],
      });
    } catch (err) {
      console.error('Failed to fetch payables:', err);
      showError(t('procurement.failedToLoadPayables'));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSuppliers = useMemo(() => {
    if (!search.trim()) return summary.suppliers;
    const q = search.trim().toLowerCase();
    return summary.suppliers.filter(s =>
      (s.supplierName || '').toLowerCase().includes(q) ||
      (s.supplierPhone || '').includes(q)
    );
  }, [summary.suppliers, search]);

  const pagedSuppliers = useMemo(() => {
    const start = (pagination.currentPage - 1) * pagination.pageSize;
    return filteredSuppliers.slice(start, start + pagination.pageSize);
  }, [filteredSuppliers, pagination.currentPage, pagination.pageSize]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  const columns = [
    {
      key: 'supplierName',
      header: t('procurement.supplier'),
      render: (row) => (
        <div className={tb.partyInfo}>
          <div className={tb.partyName}><User size={14} />{row.supplierName}</div>
          {row.supplierPhone && <div className={tb.partyMeta}><Phone size={12} />{row.supplierPhone}</div>}
        </div>
      ),
    },
    {
      key: 'totalOutstanding',
      header: t('procurement.totalOutstanding'),
      align: 'right',
      sortable: false,
      render: (row) => (
        <span className={styles.outstandingAmount}>{formatCurrency(row.totalOutstanding)}</span>
      ),
    },
    {
      key: 'buckets',
      header: t('procurement.aging0to30') + ' / ' + t('procurement.aging31to60') + ' / ' +
        t('procurement.aging61to90') + ' / ' + t('procurement.aging90plus'),
      render: (row) => (
        <div className={tb.agingPills}>
          {BUCKET_KEYS.map(key => {
            const amount = row.buckets?.[key] || 0;
            if (amount <= 0) return null;
            const aged = key === '61-90' || key === '90+';
            return (
              <span key={key} className={`${tb.agingPill} ${aged ? tb.agingPillAged : ''}`}>
                {t(`procurement.${BUCKET_LABEL_KEYS[key]}`)}: {formatCurrency(amount)}
              </span>
            );
          })}
        </div>
      ),
    },
  ];

  const renderActions = (row) => (
    <div className={tb.actionsWrapper}>
      <button
        type="button"
        className={`${tb.actionButton} ${tb.viewButton}`}
        onClick={() => navigate(`/procurement/suppliers/${row.supplierId}/statement`)}
        title={t('procurement.supplierStatement')}
      >
        <FileText size={14} />
      </button>
    </div>
  );

  return (
    <div className={styles.payablesPage}>
      <PageHeader
        title={t('procurement.payables')}
        description={t('procurement.payablesDescription')}
        icon={Wallet}
      />

      <div className={styles.summaryCards}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryCardLabel}><Wallet size={14} />{t('procurement.totalOutstanding')}</div>
          <div className={`${styles.summaryCardValue} ${styles.summaryCardValueTotal}`}>
            {formatCurrency(summary.totalOutstanding)}
          </div>
        </div>
        {BUCKET_KEYS.map(key => (
          <div className={styles.summaryCard} key={key}>
            <div className={styles.summaryCardLabel}><Clock size={14} />{t(`procurement.${BUCKET_LABEL_KEYS[key]}`)}</div>
            <div className={styles.summaryCardValue}>{formatCurrency(summary.buckets?.[key] || 0)}</div>
          </div>
        ))}
      </div>

      <FilterPanel
        filters={[]}
        values={{}}
        onChange={() => {}}
        onClear={() => handleSearchChange('')}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        searchConfig={{
          show: true,
          value: search,
          onChange: handleSearchChange,
          placeholder: t('procurement.searchBySupplierName'),
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={pagedSuppliers}
        columns={columns}
        actions={{ render: renderActions, width: '70px' }}
        loading={isLoading}
        pagination={{ ...pagination, total: filteredSuppliers.length }}
        onPaginationChange={setPagination}
        emptyMessage={t('procurement.noPayablesFound')}
        emptyDescription={t('procurement.noPayablesFoundDescription')}
        emptyIcon={<Users size={64} />}
      />
    </div>
  );
};

export default PayablesPage;
